import axios from "axios";
import { http } from "../../utils/http";
import { ActionType } from "../action-types";
import { AppThunk } from "./types";
import { ConversationDto } from "../../dto/conversation";
import { addNotifications } from "./notifications";
import { NOTIFICATION_TIMEOUT } from "../../constants";
import { setConversations } from "./conversations";

export const setContactConversationLoading = (isLoading: boolean) => ({
  type: ActionType.SET_CONTACT_CONVERSATION_LOADING,
  payload: isLoading,
});

export const setContactConversation = (item: ConversationDto | null) => ({
  type: ActionType.SET_CONTACT_CONVERSATION,
  payload: item,
});

export const resetContactConversation = () => ({
  type: ActionType.RESET_CONTACT_CONVERSATION,
});

export const fetchContactConversation =
  (conversationId: string): AppThunk<Promise<ConversationDto | null>> =>
  async (dispatch, getState) => {
    const jwt = getState().token;
    if (!jwt) throw new Error("Missing JWT!");

    dispatch(setContactConversationLoading(true));

    try {
      const res = await http.get(`/api/Conversations/${conversationId}`);
      const item = res.data as ConversationDto;
      // 1) salvo la conversazione specifica nello slice contactConversation
      dispatch(setContactConversation(item));

      // 2) la aggancio alla lista conversations esistente
      const convState = getState().conversations;
      const prevItems = convState.items ?? [];

      const getKey = (c: ConversationDto) => c.twilioSid ?? c.id;
      const newKey = getKey(item);

      const index = prevItems.findIndex((c) => getKey(c) === newKey);

      let merged: ConversationDto[];
      let newTotal = convState.totalItems;

      if (index >= 0) {
        // se esiste già, la sostituisco
        merged = [...prevItems];
        merged[index] = item;
      } else {
        // altrimenti la appendo in coda
        merged = [...prevItems, item];
        newTotal = (convState.totalItems ?? prevItems.length) + 1;
      }

      dispatch(
        setConversations({
          items: merged,
          page: convState.page,
          pageSize: convState.pageSize,
          totalItems: newTotal,
          query: convState.query,
        })
      );

      return item;
    } catch (error) {
      dispatch(setContactConversation(null));

      if (axios.isAxiosError(error)) {
        const status = error.response?.status ?? "NO_STATUS";
        const text =
          typeof error.response?.data === "string"
            ? error.response.data
            : JSON.stringify(error.response?.data ?? "");

        dispatch(
          addNotifications([
            {
              id: Date.now(),
              message: `Errore lettura conversazione: ${status}`,
              variant: "error",
              dismissAfter: NOTIFICATION_TIMEOUT,
            },
          ])
        );

        throw new Error(
          `GET /api/Conversation/${conversationId} failed REQUEST: ${status} ${text}`
        );
      }

      throw error;
    } finally {
      dispatch(setContactConversationLoading(false));
    }
  };

type NewChatPayload = {
  customerCode: string;
  phoneNumber: string;
  templateId: string;
  variables: Record<string, string>;
};

export const createNewChatFromContact =
  (payload: NewChatPayload): AppThunk<Promise<ConversationDto>> =>
  async (dispatch, getState) => {
    const jwt = getState().token;
    if (!jwt) throw new Error("Missing JWT!");

    dispatch(setContactConversationLoading(true));

    try {
      // 💡 Adatta l’URL al tuo backend reale
      const res = await http.post("/api/Conversations/new-chat", payload);
      const convo = res.data as ConversationDto;

      // Salvo nel pezzo di stato contactConversation
      dispatch(setContactConversation(convo));

      // opzionale: potresti anche aggiungerla alla lista conversations
      // dispatch(appendConversationToList(convo))

      return convo;
    } catch (error) {
      dispatch(setContactConversationLoading(false));

      if (axios.isAxiosError(error)) {
        const status = error.response?.status ?? "NO_STATUS";
        const text =
          typeof error.response?.data === "string"
            ? error.response.data
            : JSON.stringify(error.response?.data ?? "");
        throw new Error(
          `POST /api/Conversations/new-chat failed REQUEST: ${status} ${text}`
        );
      }

      throw error;
    } finally {
      dispatch(setContactConversationLoading(false));
    }
  };
